/* eslint-disable no-console, no-underscore-dangle, arrow-parens */
// Temporary, dev-only analytics debug logger.
//
// Wraps window.dataLayer.push, window.history.pushState, window.history.replaceState,
// popstate, and hashchange so we can observe everything the React app is doing
// analytics/URL-wise without attaching a debugger.
//
// Activation:
//   - Auto-on whenever process.env.NODE_ENV !== 'production'
//   - Force-on in any build:    window.__GA_DEBUG__ = true   then reload
//   - Force-on via env in any build: REACT_APP_GA_DEBUG=1
//   - Force-off in dev:         window.__GA_DEBUG_OFF__ = true   then reload
//
// Browser-console API once active:
//   window.__gaDebug.events     // dataLayer.push entries
//   window.__gaDebug.history    // history.pushState/replaceState/popstate/hashchange entries
//   window.__gaDebug.dump()     // pretty console.table summary of both buffers
//   window.__gaDebug.reset()    // clear both buffers
//   window.__gaDebug.copyJSON() // copy a JSON blob with both buffers to the clipboard

const ENV_FLAG_ON = typeof process !== 'undefined' && process.env && process.env.REACT_APP_GA_DEBUG === '1';
const RUNTIME_FLAG_ON = typeof window !== 'undefined' && window.__GA_DEBUG__ === true;
const RUNTIME_FLAG_OFF = typeof window !== 'undefined' && window.__GA_DEBUG_OFF__ === true;

const ENABLED = !RUNTIME_FLAG_OFF && (ENV_FLAG_ON || RUNTIME_FLAG_ON);

if (ENABLED && typeof window !== 'undefined' && !window.__gaDebugInstalled) {
  window.__gaDebugInstalled = true;

  const hasPerf = typeof performance !== 'undefined' && typeof performance.now === 'function';
  const now = () => (hasPerf ? performance.now() : Date.now());
  const T0 = now();
  let lastEventAt = T0;
  const monotonic = () => now() - T0;

  const shortStack = (skip = 2) => {
    try {
      const e = new Error('stack');
      return (e.stack || '')
        .split('\n')
        .slice(skip, skip + 8)
        .map(l => l.trim().replace(/^at\s+/, ''));
    }
    catch (err) {
      return [];
    }
  };

  const buf = {
    events: [],
    history: [],
  };

  const splitUrl = urlOrPath => {
    try {
      const u = new URL(urlOrPath, window.location.origin);
      return {pathname: u.pathname, search: u.search, hash: u.hash};
    }
    catch (err) {
      return {raw: String(urlOrPath)};
    }
  };

  const urlDiff = (prev, next) => {
    const a = splitUrl(prev || window.location.href);
    const b = splitUrl(next);
    const out = {};
    if (a.pathname !== b.pathname) out.pathname = `${a.pathname || ''} -> ${b.pathname || ''}`;
    if (a.search !== b.search) out.search = `${a.search || ''} -> ${b.search || ''}`;
    if (a.hash !== b.hash) out.hash = `${a.hash || ''} -> ${b.hash || ''}`;
    if (Object.keys(out).length === 0) out.same = true;
    return out;
  };

  // dataLayer.push wrap. Drupal (via google_tag module) seeds window.dataLayer
  // and pushes user_id BEFORE this module runs in production. Backfill those.
  const dl = window.dataLayer || [];
  window.dataLayer = dl;
  dl.forEach(payload => {
    buf.events.push({
      t: 0,
      dt: 0,
      seq: buf.events.length,
      payload,
      stack: ['(pre-wrap)'],
    });
  });
  const origPush = dl.push.bind(dl);
  dl.push = (...args) => {
    const ret = origPush(...args);
    args.forEach(payload => {
      const t = monotonic();
      const dt = t - lastEventAt;
      lastEventAt = t;
      const eventName = (payload && (payload.event || payload['gtm.start'])) || '(no event)';
      const stack = shortStack(3);
      const fromSpa = stack.some(line => line.indexOf('fireNow') !== -1 || line.indexOf('pageViewTracker') !== -1);
      const entry = {
        t: Math.round(t),
        dt: Math.round(dt),
        seq: buf.events.length,
        event: eventName,
        source: fromSpa ? 'spa' : 'gtm',
        payload,
        stack,
      };
      buf.events.push(entry);
      let payloadSummary = '';
      try {
        payloadSummary = JSON.stringify(payload);
      }
      catch (e) {
        payloadSummary = '<unstringifiable>';
      }
      console.groupCollapsed(
        `[gaDebug:dataLayer] +${entry.dt}ms #${entry.seq} event=${eventName} source=${entry.source} payload=${payloadSummary}`,
      );
      console.log('payload', payload);
      console.log('stack', entry.stack);
      console.groupEnd();
    });
    return ret;
  };

  // history.pushState / replaceState wrap. Use function() to preserve `this` binding
  // when called as window.history.pushState(...).
  const wrapHistoryMethod = methodName => {
    const orig = window.history[methodName];
    if (typeof orig !== 'function') return;
    window.history[methodName] = function patched(state, title, url) {
      const before = window.location.href;
      const ret = orig.call(this, state, title, url);
      const after = window.location.href;
      const t = monotonic();
      const dt = t - lastEventAt;
      lastEventAt = t;
      const diff = urlDiff(before, after);
      const kind = diff.same ? 'NO-OP' : Object.keys(diff).join('+');
      const entry = {
        t: Math.round(t),
        dt: Math.round(dt),
        seq: buf.history.length,
        type: methodName,
        kind,
        before,
        after,
        diff,
        stack: shortStack(3),
      };
      buf.history.push(entry);
      console.groupCollapsed(`[gaDebug:history.${methodName}] +${entry.dt}ms #${entry.seq} diff=${kind}`);
      console.log('before', before);
      console.log('after', after);
      console.log('diff', diff);
      console.log('stack', entry.stack);
      console.groupEnd();
      return ret;
    };
  };
  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');

  window.addEventListener('popstate', e => {
    const t = monotonic();
    const dt = t - lastEventAt;
    lastEventAt = t;
    const entry = {
      t: Math.round(t),
      dt: Math.round(dt),
      seq: buf.history.length,
      type: 'popstate',
      kind: 'popstate',
      url: window.location.href,
      state: e.state,
      stack: shortStack(2),
    };
    buf.history.push(entry);
    console.groupCollapsed(`[gaDebug:popstate] +${entry.dt}ms #${entry.seq}`);
    console.log('url', entry.url);
    console.log('state', entry.state);
    console.groupEnd();
  });

  // Pure hash flips do not trigger popstate consistently across browsers, and they
  // are the signature of map pan/zoom in this app. Track them separately.
  window.addEventListener('hashchange', e => {
    const t = monotonic();
    const dt = t - lastEventAt;
    lastEventAt = t;
    const entry = {
      t: Math.round(t),
      dt: Math.round(dt),
      seq: buf.history.length,
      type: 'hashchange',
      kind: 'hash',
      oldURL: e.oldURL,
      newURL: e.newURL,
      stack: shortStack(2),
    };
    buf.history.push(entry);
    console.groupCollapsed(`[gaDebug:hashchange] +${entry.dt}ms #${entry.seq}`);
    console.log('old', e.oldURL);
    console.log('new', e.newURL);
    console.groupEnd();
  });

  const dump = () => {
    console.group('[gaDebug] summary');
    console.log(`dataLayer events: ${buf.events.length}`);
    console.log(`history writes: ${buf.history.length}`);
    console.log('--- dataLayer events ---');
    console.table(
      buf.events.map(entry => ({
        seq: entry.seq,
        t: entry.t,
        dt: entry.dt,
        event: entry.event,
        keys: entry.payload ? Object.keys(entry.payload).join(',') : '',
      })),
    );
    console.log('--- history writes ---');
    console.table(
      buf.history.map(entry => ({
        seq: entry.seq,
        t: entry.t,
        dt: entry.dt,
        type: entry.type,
        kind: entry.kind,
        url: entry.url || entry.after || entry.newURL,
      })),
    );
    console.groupEnd();
  };

  const reset = () => {
    buf.events.length = 0;
    buf.history.length = 0;
    lastEventAt = monotonic();
    console.warn('[gaDebug] buffers cleared');
  };

  const copyJSON = () => {
    const blob = JSON.stringify(
      {
        capturedAt: new Date().toISOString(),
        url: window.location.href,
        events: buf.events,
        history: buf.history,
      },
      null,
      2,
    );
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(blob).then(
        () => console.warn(`[gaDebug] ${blob.length} bytes copied to clipboard`),
        err => console.warn('[gaDebug] clipboard write failed:', err),
      );
    }
    else {
      console.warn('[gaDebug] clipboard unavailable; returning JSON string');
    }
    return blob;
  };

  window.__gaDebug = {
    events: buf.events,
    history: buf.history,
    dump,
    reset,
    copyJSON,
  };

  // Optional GTM auto-inject for dev mode (so localhost:3000 has the same GTM that
  // Drupal injects in prod). Activated only when REACT_APP_GTM_ID is set AND no GTM
  // container is already loaded (i.e. the page wasn't served by Drupal).
  const GTM_ID = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_GTM_ID) || '';
  const gtmAlreadyLoaded = typeof window.google_tag_manager !== 'undefined'
    || (Array.isArray(window.dataLayer) && window.dataLayer.some(d => d && d['gtm.start']));
  if (GTM_ID && !gtmAlreadyLoaded) {
    console.warn(`[gaDebug] auto-injecting GTM container ${GTM_ID} (dev mode)`);
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(s);
  }

  console.warn(
    '[gaDebug] active. Logs every dataLayer.push and window.history write. '
      + 'Use __gaDebug.dump() / .reset() / .copyJSON() in the console.',
  );
}

export {};
